import {
  addMonths,
  addYears,
  fromUnixTime,
  isAfter,
  isPast,
  subMonths,
  subYears,
} from 'date-fns';
import { mutate } from 'swr';
import toast from 'react-hot-toast';
import cn from 'clsx';
import { Link, useLocation } from 'react-router-dom';
import debouce from 'lodash.debounce';
import moment from 'moment-timezone';

import { closePoll, listPollsByOwner } from '@sdk/managers/PollsManager';
import { useAuth } from '@hooks/Auth';
import { ModelPollFilterInput, PollStatus } from '@sdk/API';
import { useEffect, useMemo, useState } from 'react';
import Card from '@components/elements/Card';
import DateRangePicker from '@components/elements/DatePicker/Range';
import InputText from '@components/elements/InputText';
import { Company } from '@sdk/models';
import { fetchPollDetails, FormattedPoll } from '@utils/Poll';
import { listVotedUser } from '@sdk/managers/VotesManager';
import { listStripeChargesByPoll } from '@sdk/managers/PaymentManager';
import jsPDF from 'jspdf';
import {
  Pledged,
  PollInfo,
  Question,
  RecordPledge,
  RecordQuestion,
} from '@components/modules/Report/reportDetail';
import { renderToString } from 'react-dom/server';
import { date } from 'yup';
import { getUserByID } from '@sdk/managers/UsersManager';
import { DEFAULT_USER_FIELDS } from '../../../../utils/User';
import { forEach } from 'lodash';
function getMoment(date = new Date(), tz = 'Asia/Singapore') {
  return moment(date).tz(tz);
}

interface WithdrawPollCardProps extends React.HTMLAttributes<HTMLDivElement> {
  poll: FormattedPoll;
  index: number;
  onAction?: (pollId: string) => unknown;
  onReport?: (pollId: string) => unknown;
}

const SHADOW_COLORS = [
  'shadow-r-magenta-md',
  'shadow-r-salmon-md',
  'shadow-r-yellow-md',
  'shadow-r-cyan-md',
  'shadow-r-orange-md',
  'shadow-r-green-md',
];

const WithdrawPollCard: React.FC<WithdrawPollCardProps> = ({
  poll,
  index,
  onAction,
  onReport,
  ...props
}) => {
  // Get color, limit by length and carousell
  let bgIndex = index;
  while (bgIndex >= SHADOW_COLORS.length) {
    bgIndex = bgIndex - SHADOW_COLORS.length;
  }

  const rootCn = cn('bg-white p-4 flex items-center justify-between w-full', {
    'shadow-r-green-md': index % 6 === 0,
    'shadow-r-orange-md': index % 5 === 0,
    'shadow-r-cyan-md': index % 4 === 0,
    'shadow-r-yellow-md': index % 3 === 0,
    'shadow-r-salmon-md': index % 2 === 0,
    'shadow-r-magenta-md': index % 1 === 0,
  });

  const header = (
    <div className="flex flex-col flex-grow">
      <h2 className="overflow-hidden text-lg leading-5 break-words text-blue">
        {poll.title}
      </h2>
      {/* <p className="text-sm text-gray-dark">{poll.voteCount} votes</p> */}
    </div>
  );
  return (
    <div role="button" className={rootCn} {...props}>
      {poll.status !== PollStatus.CLOSED ? (
        <Link to={`/poll/${poll.id}`}>{header}</Link>
      ) : (
        header
      )}
      <div className="flex flex-row space-x-2 lg:space-x-4">
        <div
          role="button"
          className="px-2 font-semibold bg-opacity-50 select-none bg-green hover:bg-opacity-100"
        >
          <Link to={`/poll/${poll.id}/create`}>Duplicate</Link>
        </div>
        {poll.status !== PollStatus.CLOSED && (
          <div
            onClick={() => (onReport ? onReport(poll.id) : undefined)}
            style={{ cursor: 'pointer' }}
            role="button"
            className="px-2 font-semibold bg-opacity-50 select-none bg-orange hover:bg-opacity-100"
          >
            <div>Report</div>
          </div>
        )}

        {poll.status !== PollStatus.CLOSED &&
          poll.hasPledge &&
          poll.pledgePeriod1StartTime &&
          isPast(new Date(poll.pledgePeriod1StartTime)) && (
            <Link to={`/poll/${poll.id}/pledges`}>
              <div
                role="button"
                className="px-2 font-semibold bg-opacity-50 select-none bg-blue hover:bg-opacity-100"
              >
                <div>View Pledges</div>
              </div>
            </Link>
          )}

        {poll.status === PollStatus.CLOSED ? (
          <div className="pr-2 font-semibold select-none text-red">
            <div>DELETED</div>
          </div>
        ) : (
          <div
            role="button"
            className="px-2 font-semibold bg-opacity-50 select-none bg-green hover:bg-opacity-100"
            onClick={onAction ? () => onAction(poll.id) : undefined}
          >
            <div>Delete</div>
          </div>
        )}
      </div>
    </div>
  );
};
interface PollFilters {
  query?: string;
  focusedInput?: 'startDate' | 'endDate';
  startDate: Date;
  endDate: Date;
  private?: number;
  status?: PollStatus | 'ONGOING' | 'ANY';
}

const dropdownCn =
  'input-text rounded-md  bg-white px-2 py-4 w-full font-display text-sm outline-none placeholder-opacity-80 focus:placeholder-opacity-40 text-gray-darkest text-opacity-100 focus:text-opacity-80 placeholder-purple text-base';

type PollManagementPoll = FormattedPoll;

const AdminPollPage: React.FC = () => {
  const { dbUser, companyAdmin, company, loading } = useAuth();
  var curentPage = 1;

  const location = useLocation();

  const [filters, setFilters] = useState<PollFilters>({
    private: -1,
    startDate: subMonths(new Date(), 6),
    endDate: addMonths(new Date(), 6),
  });
  const [search, setSearch] = useState<string>('');

  // const { data, mutate } = useSwr<FeedPoll[]>(
  //   dbUser?.id ? ['/admin/polls', dbUser?.id] : null,
  //   getFeedData,
  //   {
  //     revalidateOnFocus: true,
  //     onError: (err, key) => {
  //       console.log('Failed to fetch polls', { key }, err);
  //       toast.error(
  //         err?.message || err?.errors?.[0]?.message || 'Failed to fetch polls'
  //       );
  //     },
  //   }
  // );
  const [data, setData] = useState<PollManagementPoll[]>();

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const params: any = {
      query: query.get('query'),
      startDate: query.get('startDate'),
      endDate: query.get('endDate'),
      status: query.get('status'),
      private: query.get('private'),
    };
    const newFilters: Partial<PollFilters> = {};
    if (params.startDate) {
      newFilters.startDate = fromUnixTime(params.startDate);
    } else {
      newFilters.startDate = subYears(new Date(), 1);
    }
    if (params.endDate) {
      newFilters.endDate = fromUnixTime(params.endDate);
    } else {
      newFilters.endDate = addYears(new Date(), 1);
    }
    if (params.status) newFilters.status = params.status;
    if (params.private !== null) newFilters.private = parseInt(params.private);

    setFilters(newFilters as PollFilters);
  }, [location]);

  useEffect(() => {
    if (loading || !company) return;

    const searchFilters = async () => {
      const filterParams: ModelPollFilterInput = {};
      if (filters.startDate)
        filterParams.startTime = {
          ge: filters.startDate.toISOString(),
        };
      if (filters.endDate)
        filterParams.endTime = {
          le: filters.endDate.toISOString(),
        };
      if (filters.status) {
        if (filters.status === 'ONGOING')
          filterParams.status = {
            ne: PollStatus.CLOSED,
          };
        else if (filters.status !== 'ANY')
          filterParams.status = { eq: filters.status };
      }
      if (filters.private !== undefined && filters.private !== -1)
        filterParams.private = {
          eq: filters.private,
        };

      try {
        if (loading) return;
        if (!companyAdmin) {
          throw Error('Company not found.');
        }

        const getListPolls: any = await listPollsByOwner(
          [
            {
              items: [
                'id',
                'title',
                'description',
                'status',
                'owner',
                'hasPledge',
                'startTime',
                'endTime',
                'image { key bucket region }',
                'video',
                'status',
                'private',
                'pledgePeriod1StartTime',
                'pledgePeriod1EndTime',
                {
                  options: ['id', 'description'],
                },
                { voteResults: [{ items: ['count'] }] },
              ],
            },
            'nextToken',
          ],
          companyAdmin.owner,
          {
            // status: { ne: PollStatus.CLOSED },
            companyID: { eq: company.companyID },
          }
        );

        const {
          data: {
            result: { items },
          },
        } = getListPolls;

        setData(items as PollManagementPoll[]);
      } catch (err) {
        console.log('Failed to get poll data', err);
        toast.error('Something went wrong');
      } finally {
        // toast.dismiss(toastId);
      }
    };
    searchFilters();
  }, [company, companyAdmin, filters, loading]);

  const InformationReport = (pdfContainer: HTMLDivElement, pollsData) => {
    const pdfElement = document.createElement('div');
    pdfElement.innerHTML = renderToString(
      <PollInfo user={dbUser} page={curentPage} data={pollsData} />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
  };

  const QuestionReport = (pdfContainer: HTMLDivElement, detail, pollInfo) => {
    let data = [...pollInfo.selections];
    let representativesUsageRecord;
    const pdfElement = document.createElement('div');
    if (data.length >= 15) {
      representativesUsageRecord = [...data.filter((item, i) => i < 15)];
      data.splice(0, 15);
    } else {
      representativesUsageRecord = [...data];
      data.splice(0, data.length);
    }

    pdfElement.innerHTML = renderToString(
      <Question
        user={dbUser}
        selections={representativesUsageRecord}
        detailData={detail}
        page={curentPage}
      />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
    let recordPages = Math.ceil(data.length / 20);
    for (let i = 0; i < recordPages; i++) {
      let records = [] as any;
      let recordElement = document.createElement('div');
      if (data.length >= 20) {
        records = [...data.filter((item, i) => i <= 19)];
        data.splice(0, 20);
      } else records = [...data];

      recordElement.innerHTML = renderToString(
        <RecordQuestion user={dbUser} page={curentPage} selections={records} />
      ).trim();
      pdfContainer.appendChild(recordElement);
      curentPage++;
    }
  };
  const PledgeReport = (pdfContainer: HTMLDivElement, pollInfo) => {
    let data = [...pollInfo.users];
    let representativesUsageRecord;
    const pdfElement = document.createElement('div');
    if (data.length >= 15) {
      representativesUsageRecord = [...data.filter((item, i) => i < 15)];
      data.splice(0, 15);
    } else {
      representativesUsageRecord = [...data];
      data.splice(0, data.length);
    }

    pdfElement.innerHTML = renderToString(
      <Pledged
        user={dbUser}
        selections={pollInfo.selections}
        users={representativesUsageRecord}
        data={pollInfo}
        page={curentPage}
      />
    ).trim();
    pdfContainer.appendChild(pdfElement);
    curentPage++;
    let recordPages = Math.ceil(data.length / 20);
    console.log(recordPages);
    for (let i = 0; i < recordPages; i++) {
      let records = [] as any;
      let recordElement = document.createElement('div');
      if (data.length >= 20) {
        records = [...data.filter((item, i) => i <= 19)];
        data.splice(0, 20);
      } else records = [...data];

      recordElement.innerHTML = renderToString(
        <RecordPledge user={dbUser} page={curentPage} users={records} />
      ).trim();
      pdfContainer.appendChild(recordElement);
      curentPage++;
    }
  };

  const onReport = async (pollId) => {
    const pollDetail = await fetchPollDetails('', pollId);
    const pledges = await listStripeChargesByPoll(pollId);
    const pledgesUsers = (
      await Promise.all(
        pledges.map((pledge) => getUserByID(DEFAULT_USER_FIELDS, pledge.userID))
      )
    ).map((user): any => {
      // @ts-ignore
      return user.data.result;
    });
    let selections: any[] = pollDetail?.poll?.options || [];
    if (selections.length) {
      // eslint-disable-next-line array-callback-return
      const total = selections
        .map((x) => x.count)
        .filter(Boolean)
        .reduce((a, b) => a + b, 0);
      selections = selections
        .map((x) => {
          // @ts-ignore
          x.count = +x.count || 0;
          return {
            price: +x.detail.price || 0,
            title: x.title,
            count: x.count,
            percentage: Math.round((x.count * 100) / total),
          };
        })
        .sort((a, b) => b.count - a.count);
      console.log('options: ', selections);
    }
    const pledgeUserData = pledgesUsers.map((item) => {
      return {
        username: item?.username || '',
        email: item?.email || '',
        phone: item?.phone || '',
      };
    });

    const info = {
      title: pollDetail?.poll?.title || '',
      description: pollDetail?.poll?.description || '',
      start: pollDetail?.poll?.startTime
        ? getMoment(new Date(pollDetail?.poll?.startTime)).format('DD MMM YYYY')
        : '',
      end: pollDetail?.poll?.endTime
        ? getMoment(new Date(pollDetail?.poll?.endTime)).format('DD MMM YYYY')
        : '',
      pledgeStart: pollDetail?.poll?.pledgePeriod1StartTime
        ? getMoment(new Date(pollDetail?.poll?.pledgePeriod1StartTime)).format(
            'DD MMM YYYY'
          )
        : '',
      pledgeEnd: pollDetail?.poll?.pledgePeriod1EndTime
        ? getMoment(new Date(pollDetail?.poll?.pledgePeriod1EndTime)).format(
            'DD MMM YYYY'
          )
        : '',
      numOfVotes:
        pollDetail?.poll?.voteCount +
        (pollDetail?.subPolls
          ? pollDetail?.subPolls?.reduce((r, a) => {
              r += a.voteCount;
              return r;
            }, 0)
          : 0),
      numOfPledges: pledges?.length || 0,
      totalPledgeAmount:
        (pledges?.map((x) => x.amount).reduce((a, b) => a + +b, 0) || 0) / 100,
      selections,
      users: pledgeUserData,
    };
    console.log('info: ', info);
    console.log(pollDetail);
    var pdfContainer = document.createElement('div');
    InformationReport(pdfContainer, info);
    QuestionReport(pdfContainer, pollDetail?.poll, info);
    pollDetail?.subPolls?.forEach((item) => {
      let selectionsSub: any[] = item.options || [];
      if (selectionsSub.length) {
        // eslint-disable-next-line array-callback-return
        const total = selectionsSub
          .map((x) => x.count)
          .filter(Boolean)
          .reduce((a, b) => a + b, 0);
        selectionsSub = selectionsSub
          .map((x) => {
            // @ts-ignore
            x.count = +x.count || 0;
            return {
              price: +x.detail.price || 0,
              title: x.title,
              count: x.count,
              percentage: Math.round((x.count * 100) / total),
            };
          })
          .sort((a, b) => b.count - a.count);
      }
      const infoSub = {
        title: item.title || '',
        description: item.description || '',
        start: pollDetail?.poll?.startTime
          ? getMoment(new Date(item.startTime)).format('DD MMM YYYY')
          : '',
        end: pollDetail?.poll?.endTime
          ? getMoment(new Date(item.endTime)).format('DD MMM YYYY')
          : '',
        pledgeStart: item.pledgePeriod1StartTime
          ? getMoment(new Date(item.pledgePeriod1StartTime)).format(
              'DD MMM YYYY'
            )
          : '',
        pledgeEnd: item.pledgePeriod1EndTime
          ? getMoment(new Date(item.pledgePeriod1EndTime)).format('DD MMM YYYY')
          : '',
        numOfVotes: item.voteCount || 0,
        numOfPledges: pledges?.length || 0,
        totalPledgeAmount:
          (pledges?.map((x) => x.amount).reduce((a, b) => a + +b, 0) || 0) /
          100,
        selections: selectionsSub,
        users: pledgeUserData,
      };
      console.log(infoSub);
      QuestionReport(pdfContainer, item, infoSub);
    });
    PledgeReport(pdfContainer, info);
    pdfContainer.classList.add('report-pdf-container');
    let pdf = new jsPDF('p', 'pt', 'A4');
    pdf.html(pdfContainer, {
      fontFaces: [
        {
          family: 'Space Grotesk',
          src: [
            {
              format: 'truetype',
              url: '/space-grotesk.ttf',
            },
          ],
        },
        {
          family: 'Arial',
          src: [
            {
              format: 'truetype',
              url: '/arial.ttf',
            },
          ],
        },
      ],

      callback: () => {
        var pageCount = pdf.internal.pages.length - 1;
        pdf.deletePage(pageCount);
        pdf.save(
          'Report_' +
            pollDetail?.poll.title.trim() +
            '_' +
            new Date().getTime() +
            '.pdf'
        );
        curentPage = 1;
      },
    });
  };

  const withdrawPoll = async (pollId: string) => {
    try {
      const withdrawPollAction = async () => {
        const closePollRes = (await closePoll(pollId)) as any;
        await mutate(['/admin/polls', dbUser?.id]);
        return closePollRes;
      };
      const res = await toast.promise(withdrawPollAction(), {
        loading: 'Closing poll...',
        success: 'Succesfully closed poll',
        error: 'Failed to close poll',
      });
      console.log(res);
    } catch (err: any) {
      console.error(err);
      toast.error(err?.message || 'Unexpected error');
    }
  };

  const handleSearchChange = (value: string) => {
    setSearch(value.toLowerCase().replace(/ /g, ''));
  };

  const debouncedSearch = useMemo(() => {
    return debouce(handleSearchChange, 150);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  });

  console.log('filters', filters);

  const filteredData =
    data?.filter((poll) => {
      if (
        search &&
        search !== '' &&
        poll.title
          .toLowerCase()
          .replace(/ /g, '')
          .indexOf(search.toLowerCase()) === -1
      ) {
        return false;
      }

      if (filters.status && filters.status !== 'ANY') {
        if (filters.status === 'ONGOING' && poll.status !== undefined) {
          if ([PollStatus.END, PollStatus.CLOSED].includes(poll.status as any))
            return false;
        } else if (poll.status !== filters.status) return false;
      }
      if (filters.private !== undefined && filters.private !== -1) {
        if (poll.private !== filters.private) return false;
      }

      if (
        poll.startTime &&
        isAfter(filters.startDate, new Date(poll.startTime))
      ) {
        return false;
      }

      if (poll.endTime && isAfter(new Date(poll.endTime), filters.endDate)) {
        return false;
      }

      return true;
    }) || [];

  return (
    <div className="flex flex-col min-h-screen px-4 pt-16 pb-32 bg-blue-light">
      <div className="w-full mb-6">
        <h1 className="text-4xl text-white">Poll Management</h1>
      </div>
      <Card
        className="px-3 py-2 mb-4"
        color="black"
        bgColor="white"
        shadowColor="blue"
      >
        <h2 className="mb-3 text-xl">Filters</h2>
        <div className="grid grid-cols-12 gap-4">
          <div className="col-span-12">
            <InputText
              defaultValue={new URLSearchParams(location.search).get('query')}
              placeholder="Search for Poll titles"
              onChange={debouncedSearch}
            />
          </div>
          <div className="col-span-3">
            <div className="pb-2 text-lg font-bold">Status</div>
            <select
              className={cn(dropdownCn, 'border-gray-dark')}
              value={filters.status}
              onChange={(ev) => {
                setFilters({
                  ...filters,
                  status: ev.target.value as any,
                });
              }}
            >
              <option className="capitalize" value="ANY">
                Any
              </option>
              <option className="capitalize" value="ONGOING">
                Ongoing
              </option>
              <option className="capitalize" value={PollStatus.PLEDGING1}>
                Ongoing Pledging
              </option>
              <option className="capitalize" value={PollStatus.SWING}>
                Swing Vote
              </option>
              <option className="capitalize" value={PollStatus.END}>
                Ended
              </option>
            </select>
          </div>
          <div className="col-span-2">
            <div className="pb-2 text-lg font-bold">Visibility</div>
            <select
              className={cn(dropdownCn, 'border-gray-dark')}
              value={filters.private}
              onChange={(ev) => {
                setFilters({
                  ...filters,
                  private: parseInt(ev.target.value),
                });
              }}
            >
              <option className="capitalize" value={-1}>
                Any
              </option>
              <option className="capitalize" value={0}>
                Public
              </option>
              <option className="capitalize" value={2}>
                Enterprise
              </option>
              <option className="capitalize" value={1}>
                Private
              </option>
            </select>
          </div>
          <div className="col-span-7">
            <DateRangePicker
              inputCn="text-gray-darkest shadow-none border-gray-dark border-3"
              startDate={filters.startDate}
              endDate={filters.endDate}
              dismiss
              onChange={(data) => {
                const payload: any = {};
                if (data.startDate) {
                  if (data.startDate > data.endDate) {
                    payload.endDate = data.startDate;
                  }
                  payload.startDate = data.startDate;
                }
                if (data.endDate) {
                  if (data.endDate < data.startDate) {
                    payload.startDate = data.endDate;
                  }
                  payload.endDate = data.endDate;
                }

                setFilters({
                  ...filters,
                  ...payload,
                });
              }}
            />
          </div>
        </div>
        {/* <div>{JSON.stringify(filters)}</div> */}
        {data && (
          <div className="mt-4 text-sm text-semibold">
            Found {filteredData.length} matches
          </div>
        )}
      </Card>
      {filteredData.length === 0 && (
        <Card bgColor="white" color="black" className="p-4 text-center">
          No Polls found matching filters.
        </Card>
      )}
      {data && (
        <div className="w-full space-y-4">
          {filteredData.map((poll, index) => (
            <WithdrawPollCard
              key={`withdrawpoll-card-${poll.id}`}
              onReport={onReport}
              index={index}
              poll={poll}
              onAction={withdrawPoll}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminPollPage;
